import { styled } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

import { NextIcon } from '../../../icons';
import { SUB_TOPIC_LIST_WIDE_QUERY } from '../SubTopicList/subTopicListContainer';

type Props = {
  name: string;
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** State of the sub-topic, typically a `Tag variantIcon="state"` (En cours, Terminé, Archivé). */
  status?: ReactNode;
  /** Mutes the name of an archived sub-topic. */
  archived?: boolean;
  /** Secondary controls (resource menu). Rendered above the row link, never nested in it. */
  action?: ReactNode;
  'data-testid'?: string;
};

type OwnerState = {
  archived: boolean;
};

const Row = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gridTemplateAreas: `
    "name trailing"
    "status trailing"`,
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  alignItems: 'center',
  minHeight: theme.spacing(9),
  paddingBlock: theme.spacing(1.5),
  paddingInline: theme.spacing(2),
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:has(a:focus-visible)': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  [SUB_TOPIC_LIST_WIDE_QUERY]: {
    gridTemplateColumns: 'minmax(0, 1fr) auto auto',
    gridTemplateAreas: '"name status trailing"',
    columnGap: theme.spacing(2),
    minHeight: theme.spacing(7),
    paddingBlock: theme.spacing(1),
    paddingInline: theme.spacing(3),
  },
}));

const NameLink = styled('a')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.body1Emphasis,
  gridArea: 'name',
  minWidth: 0,
  color: ownerState.archived ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: 'none',
  overflowWrap: 'anywhere',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
  },
  '&:focus-visible': {
    outline: 'none',
  },
  [SUB_TOPIC_LIST_WIDE_QUERY]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflowWrap: 'normal',
  },
}));

const Status = styled('span')({
  gridArea: 'status',
  display: 'flex',
  justifySelf: 'start',
  minWidth: 0,
});

const Trailing = styled('span')(({ theme }) => ({
  gridArea: 'trailing',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

const ActionSlot = styled('span')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  marginRight: theme.spacing(-1),
}));

/**
 * One sub-topic of a topic, inside a `SubTopicList`.
 * The name is the row link (its hit area covers the whole row) and opens the sub-topic; its state
 * sits under the name on narrow widths and beside it on wide ones. The optional action sits above
 * the link so the resource menu stays a separate control.
 */
export function SubTopicListItem(props: Props) {
  const ownerState = { archived: Boolean(props.archived) };

  return (
    <li data-testid={props['data-testid'] ?? 'sub-topic-list-item'}>
      <Row>
        <NameLink href={props.href} onClick={props.onClick} ownerState={ownerState}>
          {props.name}
        </NameLink>
        {props.status ? <Status>{props.status}</Status> : null}
        <Trailing>
          {props.action ? (
            <ActionSlot>{props.action}</ActionSlot>
          ) : (
            <NextIcon fontSize="small" aria-hidden="true" />
          )}
        </Trailing>
      </Row>
    </li>
  );
}

export type { Props as SubTopicListItemProps };
